import { useEffect, useState } from "react";
import { Globe, Eye, EyeOff } from "lucide-react";
import { useSettings } from "@/context/SettingsContext";
import { useSettingsState } from "@/context/SettingsStateContext";
import { useWebsites } from "@/context/WebsitesContext";
import { useWordPress } from "@/context/WordPressContext";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { FloatingSaveButton } from "@/components/ui/FloatingSaveButton";
import CustomLanguageInputModal from "@/components/dialogs/CustomLanguageInputModal";
import GeneratedCategoryIdeasDialog from "@/components/dialogs/GeneratedCategoryIdeasDialog";
import WordPressAuthenticationDialog from "@/components/dialogs/WordPressAuthenticationDialog";
import AIImageGenerationCard from "@/components/settings/cards/AIImageGenerationCard";
import AIModelConfigCard from "@/components/settings/cards/AIModelConfigCard";
import ContentFormattingCard from "@/components/settings/cards/ContentFormattingCard";
import CmsSelectionCard from "@/components/settings/cards/CmsSelectionCard";
import ImportLimitsCard from "@/components/settings/cards/ImportLimitsCard";
import KeywordsCard from "@/components/settings/cards/KeywordsCard";
import LanguageCard from "@/components/settings/cards/LanguageCard";
import PublicationSettingsCard from "@/components/settings/cards/PublicationSettingsCard";
import SocialMediaCard from "@/components/settings/cards/SocialMediaCard";
import TemplateRulesCard from "@/components/settings/cards/TemplateRulesCard";
import WordPressIntegrationCard from "@/components/settings/cards/WordPressIntegrationCard";
import WordPressCategoriesCard from "@/components/settings/cards/WordPressCategoriesCard";
import WritingStyleCard from "@/components/settings/cards/WritingStyleCard ";
import ContentPoliciesCard from "@/components/settings/cards/ContentPoliciesCard";
import SettingsPageLoader from "@/components/loaders/SettingsPageLoader";
import DataTransferCard from "@/components/settings/cards/DataTransferCard";
import BacklinkRewardsCard from "@/components/settings/cards/BacklinkRewardsCard";
import config from "@/config";

const Settings = () => {
  const [showDebugIds, setShowDebugIds] = useState(false);
  const {
    postingFrequency,
    writingStyle,
    subjectMatters,
    formattemplate,
    imagePrompt,
    imageModel,
    negativePrompt,
    weeklyPlanningDay,
    contentPolicies,
    isLoading: settingsLoading,
  } = useSettings();
  const { currentWebsite } = useWebsites();
  const { isLoading: wpLoading } = useWordPress();
  const {
    styleInput,
    setStyleInput,
    subjects,
    setSubjects,
    setHtmlTemplate,
    isSocialMediaEnabled,
    setIsSocialMediaEnabled,
    setHasUnsavedChanges,
    lastSavedValues,
    setImageGenerationEnabled,
  } = useSettingsState();

  useEffect(() => {
    if (!settingsLoading) {
      setStyleInput(writingStyle);
      setSubjects(subjectMatters);
      setHtmlTemplate(formattemplate);
      console.log(
        "Using WordPress template from context:",
        formattemplate.substring(0, 50) + "..."
      );
    }
  }, [settingsLoading, writingStyle, subjectMatters, formattemplate]);

  /**
   * Update the effect to track changes with less frequency
   */
  useEffect(() => {
    // Don't set changes during initialization or loading
    if (settingsLoading) return;

    // Compare with previous values to determine if there are actual changes
    const hasChanges =
      postingFrequency !== lastSavedValues?.posting_frequency ||
      styleInput !== lastSavedValues?.writing_style ||
      JSON.stringify(subjects) !==
        JSON.stringify(lastSavedValues?.subject_matters) ||
      formattemplate !== lastSavedValues?.format_template ||
      imagePrompt !== lastSavedValues?.image_prompt ||
      imageModel !== lastSavedValues?.image_model ||
      negativePrompt !== lastSavedValues?.negative_prompt ||
      weeklyPlanningDay !== lastSavedValues?.weekly_planning_day ||
      contentPolicies !== lastSavedValues?.content_policies;

    if (hasChanges) {
      setHasUnsavedChanges(true);
    }
  }, [
    postingFrequency,
    styleInput,
    subjects,
    formattemplate,
    imagePrompt,
    imageModel,
    negativePrompt,
    weeklyPlanningDay,
    contentPolicies,
    settingsLoading,
    lastSavedValues,
  ]);

  /**
   * Effect to initialize social media enabled state
   */
  useEffect(() => {
    if (currentWebsite && currentWebsite.enable_some !== isSocialMediaEnabled) {
      setIsSocialMediaEnabled(currentWebsite.enable_some || false);
    }
  }, [currentWebsite, isSocialMediaEnabled]);

  /**
   * Add effect to sync AI image generation state
   */
  useEffect(() => {
    if (currentWebsite) {
      setImageGenerationEnabled(
        currentWebsite.enable_ai_image_generation || false
      );
    }
  }, [currentWebsite]);

  /**
   * Optionally, show a toast if success=1 is present in the URL
   */
  useEffect(() => {
    const searchParams = new URLSearchParams(window.location.search);
    if (searchParams.get("success") === "1") {
      
      // Remove the success param from the URL
      window.history.replaceState(null, "", window.location.pathname);
    }
  }, []);

  return (
    <div className="overflow-y-scroll">
      <div className="max-w-6xl mx-auto">
        <div className="flex gap-6">
          <div className="flex-1 space-y-6">
            {currentWebsite && (
              <Alert className="bg-muted/50 border-muted">
                <div className="flex items-center gap-2">
                  <Globe className="h-4 w-4" />
                  <AlertDescription className="flex-1">
                    Managing settings for <strong>{currentWebsite.name}</strong>
                    {showDebugIds && (
                      <div className="text-xs text-muted-foreground mt-1">
                        Website ID: <code className="bg-muted px-1 rounded">{currentWebsite.id}</code>
                        {currentWebsite.organisation_id && (
                          <> • Organisation ID: <code className="bg-muted px-1 rounded">{currentWebsite.organisation_id}</code></>
                        )}
                      </div>
                    )}
                  </AlertDescription>
                  <button
                    onClick={() => setShowDebugIds(!showDebugIds)}
                    className="text-muted-foreground hover:text-foreground transition-colors flex-shrink-0"
                    title={showDebugIds ? "Hide debug IDs" : "Show debug IDs"}
                  >
                    {showDebugIds ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                </div>
              </Alert>
            )}

            {/* CMS Selection - before WordPress integration */}
            <CmsSelectionCard />

            

            {settingsLoading || wpLoading ? (
              <SettingsPageLoader />
            ) : (
              <>
                {currentWebsite?.cms !== "wordpress" && (
                  <WordPressIntegrationCard />
                )}

                <WordPressCategoriesCard />
                <PublicationSettingsCard />
                <KeywordsCard />
                <WritingStyleCard />
                <ContentPoliciesCard />
                <ContentFormattingCard />
                <LanguageCard />
                <TemplateRulesCard />
                <ImportLimitsCard />
                <AIImageGenerationCard />
                <AIModelConfigCard />
                <SocialMediaCard />
                <BacklinkRewardsCard />
                <FloatingSaveButton />
              </>
            )}
          </div>
        </div>

        {/* Add Retrigger Data Transfer Button - only show on localhost */}
        {config.frontendApp.mode === "development" && <DataTransferCard />}
      </div>

      <WordPressAuthenticationDialog />
      <GeneratedCategoryIdeasDialog />
      <CustomLanguageInputModal />
    </div>
  );
};

export default Settings;
